import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../../services/api'
import toast from 'react-hot-toast'

// ─── Helpers para APIs externas ─────────────────────────────────────────────
const uid = () => Math.random().toString(36).slice(2, 9)

const API_VACIA = () => ({
  id: uid(), nombre: '', descripcion: '', urlBase: '', activo: true,
  authTipo: 'header', authHeader: 'X-API-Key', authValor: '',
  endpoints: [],
})

const ENDPOINT_VACIO = () => ({
  id: uid(), path: '', metodo: 'GET', descripcion: '', parametros: [],
})

const PARAM_VACIO = () => ({ nombre: '', descripcion: '', requerido: false })

const AUTH_TIPOS = [
  { val: 'none',   label: 'Sin autenticación' },
  { val: 'header', label: 'API Key en Header' },
  { val: 'bearer', label: 'Bearer Token' },
  { val: 'basic',  label: 'Basic Auth (user:pass)' },
  { val: 'query',  label: 'API Key en Query Param' },
]

// ─── Plantillas por industria ───────────────────────────────────────────────
const PLANTILLAS = [
  {
    id: 'tienda',
    icon: '🛒',
    nombre: 'Tienda / E-commerce',
    campos: {
      tono: 'amigable',
      objetivo: 'ventas',
      descripcionNegocio: 'Somos una tienda online especializada en [productos]. Ofrecemos envíos a todo el país con garantía de satisfacción.',
      productosServicios: 'Lista tus productos principales con precios:\n- Producto A: $XX\n- Producto B: $XX\n- Producto C: $XX',
      procesosVenta: '1. Saluda al cliente por su nombre\n2. Pregunta qué producto busca\n3. Presenta opciones con precios\n4. Informa sobre envíos y tiempos\n5. Ofrece métodos de pago\n6. Confirma el pedido',
      politicasNegocio: 'Envíos: 3-5 días hábiles. Devoluciones: hasta 30 días con producto en perfecto estado. Garantía: 6 meses en todos los productos.',
      metodosPago: 'Transferencia bancaria, tarjeta de crédito/débito, efectivo contra entrega',
      restricciones: 'No hagas descuentos sin autorización. No confirmes stock si no está en el catálogo. No des información de proveedores.',
    }
  },
  {
    id: 'inmobiliaria',
    icon: '🏠',
    nombre: 'Inmobiliaria',
    campos: {
      tono: 'profesional',
      objetivo: 'citas',
      descripcionNegocio: 'Somos una inmobiliaria especializada en compra, venta y alquiler de propiedades en [ciudad/zona]. Trabajamos con propiedades residenciales y comerciales.',
      productosServicios: 'Servicios que ofrecemos:\n- Venta de apartamentos y casas\n- Alquiler de locales comerciales\n- Asesoría en compra de terrenos\n- Gestión de arrendamientos',
      procesosVenta: '1. Pregunta si busca comprar, vender o alquilar\n2. Solicita zona de interés y presupuesto\n3. Presenta propiedades disponibles\n4. Agenda una visita o videollamada\n5. Conecta con el asesor comercial',
      politicasNegocio: 'Las visitas son sin costo. Comisión de venta: 3%. Todas las propiedades están verificadas legalmente.',
      metodosPago: 'Financiamiento bancario, pago de contado, crédito hipotecario',
      restricciones: 'No confirmes precios definitivos sin consultar al asesor. No hagas promesas sobre tasas de interés. Siempre agenda una cita para cierre.',
    }
  },
  {
    id: 'restaurante',
    icon: '🍽️',
    nombre: 'Restaurante / Delivery',
    campos: {
      tono: 'amigable',
      objetivo: 'pedidos',
      descripcionNegocio: 'Somos un restaurante de [tipo de cocina] con servicio de delivery y atención en local. Usamos ingredientes frescos y preparación artesanal.',
      productosServicios: 'Menú principal:\n- Plato 1: $XX\n- Plato 2: $XX\n- Bebidas desde: $XX\nPromoción del día: [promo]',
      procesosVenta: '1. Da la bienvenida y pregunta si es para delivery o para comer en local\n2. Comparte el menú del día\n3. Toma el pedido con detalles\n4. Para delivery: pide la dirección y confirma tiempo de entrega\n5. Informa el total y método de pago',
      politicasNegocio: 'Delivery: 30-45 minutos. Pedido mínimo delivery: $15. Zona de cobertura: [zona]. Aceptamos cambios de pedido hasta 5 minutos después de confirmado.',
      metodosPago: 'Efectivo, transferencia, tarjeta en local',
      restricciones: 'No ofrezcas descuentos sin autorización. Si el pedido tiene más de 10 items, escala a un agente. No confirmes envíos fuera de la zona de cobertura.',
    }
  },
  {
    id: 'servicios',
    icon: '🔧',
    nombre: 'Servicios / Consultora',
    campos: {
      tono: 'profesional',
      objetivo: 'citas',
      descripcionNegocio: 'Somos una empresa de servicios especializada en [área]. Contamos con más de X años de experiencia y un equipo certificado.',
      productosServicios: 'Servicios que ofrecemos:\n- Servicio A: desde $XX\n- Servicio B: desde $XX\n- Consultoría inicial: gratuita',
      procesosVenta: '1. Pregunta cuál es el problema o necesidad\n2. Presenta el servicio más adecuado\n3. Informa tiempos y costos aproximados\n4. Agenda una consulta inicial gratuita\n5. Confirma disponibilidad con el equipo',
      politicasNegocio: 'Primera consulta gratuita de 30 minutos. Presupuesto sin costo. Garantía de satisfacción en todos los servicios.',
      metodosPago: 'Transferencia, efectivo, pago en cuotas',
      restricciones: 'No des precios definitivos sin evaluar el caso. No hagas promesas de resultados específicos. Siempre aclara que el presupuesto final lo da el especialista.',
    }
  },
  {
    id: 'salud',
    icon: '⚕️',
    nombre: 'Clínica / Salud',
    campos: {
      tono: 'formal',
      objetivo: 'citas',
      descripcionNegocio: 'Somos una clínica especializada en [especialidad]. Contamos con médicos certificados y equipos de última generación.',
      productosServicios: 'Especialidades y servicios:\n- Consulta general: $XX\n- Especialidades: [lista]\n- Exámenes de laboratorio\n- Rayos X y ecografías',
      procesosVenta: '1. Saluda con calidez y profesionalismo\n2. Pregunta si es consulta nueva o seguimiento\n3. Solicita la especialidad requerida\n4. Verifica disponibilidad de agenda\n5. Agenda la cita con nombre completo y CI\n6. Confirma fecha, hora y médico asignado',
      politicasNegocio: 'Citas con 24h de anticipación. Cancelaciones con mínimo 2 horas de aviso. Los resultados de exámenes se entregan en 24-48 horas.',
      metodosPago: 'Efectivo, tarjeta, seguros médicos afiliados',
      restricciones: 'NUNCA des diagnósticos. No recomiendes medicamentos específicos. No confirmes urgencias por este canal, indica llamar al [número de emergencias]. Deriva todo tema médico al profesional.',
    }
  },
  {
    id: 'educacion',
    icon: '📚',
    nombre: 'Academia / Educación',
    campos: {
      tono: 'amigable',
      objetivo: 'informacion',
      descripcionNegocio: 'Somos una academia de [área educativa]. Ofrecemos cursos presenciales y online con certificación incluida.',
      productosServicios: 'Cursos disponibles:\n- Curso A: $XX (X semanas)\n- Curso B: $XX (X semanas)\n- Modalidad online y presencial',
      procesosVenta: '1. Pregunta qué área de estudio le interesa\n2. Presenta los cursos disponibles con duración y precio\n3. Informa sobre certificación y modalidades\n4. Ofrece hablar con un asesor para más detalles\n5. Comparte el link de inscripción si está decidido',
      politicasNegocio: 'Inscripción abierta todo el año. Inicio de clases: [fechas]. Pago en cuotas disponible. Certificado incluido al finalizar.',
      metodosPago: 'Transferencia, tarjeta, cuotas sin interés',
      restricciones: 'No confirmes becas o descuentos sin autorización. No garantices resultados de empleo. No inscribas menores de edad sin consentimiento de padres.',
    }
  },
]

const TONOS = [
  { val: 'amigable', label: '😊 Amigable y cercano', desc: 'Usa emojis moderados, tuteo, lenguaje cálido' },
  { val: 'profesional', label: '👔 Profesional', desc: 'Formal pero accesible, sin emojis excesivos' },
  { val: 'formal', label: '🎩 Muy formal', desc: 'Usted, lenguaje corporativo, sin emojis' },
  { val: 'casual', label: '🤙 Casual y divertido', desc: 'Relajado, emojis frecuentes, muy cercano' },
]

const OBJETIVOS = [
  { val: 'ventas', label: '💰 Vender productos/servicios' },
  { val: 'citas', label: '📅 Agendar citas y reuniones' },
  { val: 'pedidos', label: '📦 Tomar pedidos' },
  { val: 'soporte', label: '🛠️ Soporte y atención al cliente' },
  { val: 'informacion', label: '📋 Dar información y resolver dudas' },
]

const IDIOMAS = [
  { val: 'español', label: 'Español' },
  { val: 'ingles', label: 'English' },
  { val: 'portugues', label: 'Português' },
  { val: 'español_ingles', label: 'Español + English (detecta automático)' },
]

// ─── Generador de prompt maestro ─────────────────────────────────────────────
function generarPromptMaestro(config) {
  const tono = TONOS.find(t => t.val === config.tono)
  const objetivo = OBJETIVOS.find(o => o.val === config.objetivo)
  const idioma = IDIOMAS.find(i => i.val === config.idioma)

  let prompt = `Eres ${config.nombreBot || 'Asistente'}, el asistente virtual de ${config.nombreNegocio || 'nuestra empresa'}.`

  if (config.descripcionNegocio) {
    prompt += `\n\n## SOBRE EL NEGOCIO\n${config.descripcionNegocio}`
  }

  if (tono) {
    prompt += `\n\n## TONO Y ESTILO DE COMUNICACIÓN\n${tono.desc}.`
    if (config.tono === 'amigable' || config.tono === 'casual') {
      prompt += ' Usa emojis de forma moderada para hacer la conversación más dinámica.'
    } else if (config.tono === 'formal' || config.tono === 'profesional') {
      prompt += ' Mantén siempre un lenguaje respetuoso y profesional.'
    }
  }

  if (idioma) {
    if (config.idioma === 'español_ingles') {
      prompt += `\n\n## IDIOMA\nDetecta el idioma en que te escribe el cliente y responde en el mismo idioma. Si no queda claro, responde en español.`
    } else {
      prompt += `\n\n## IDIOMA\nResponde siempre en ${idioma.label}.`
    }
  }

  if (objetivo) {
    prompt += `\n\n## OBJETIVO PRINCIPAL\nTu objetivo principal es: ${objetivo.label}.`
  }

  if (config.productosServicios) {
    prompt += `\n\n## PRODUCTOS / SERVICIOS\n${config.productosServicios}`
  }

  if (config.procesosVenta) {
    prompt += `\n\n## PROCESO DE ATENCIÓN (sigue este flujo)\n${config.procesosVenta}`
  }

  if (config.politicasNegocio) {
    prompt += `\n\n## POLÍTICAS DEL NEGOCIO\n${config.politicasNegocio}`
  }

  if (config.metodosPago) {
    prompt += `\n\n## MÉTODOS DE PAGO\n${config.metodosPago}`
  }

  if (config.datosContacto) {
    prompt += `\n\n## INFORMACIÓN DE CONTACTO\n${config.datosContacto}`
  }

  if (config.preguntasFrecuentes) {
    prompt += `\n\n## PREGUNTAS FRECUENTES\n${config.preguntasFrecuentes}`
  }

  if (config.horario) {
    prompt += `\n\n## HORARIO DE ATENCIÓN\n${config.horario}`
  }

  if (config.restricciones) {
    prompt += `\n\n## LO QUE NO DEBES HACER (restricciones importantes)\n${config.restricciones}`
  }

  if (config.frasesProhibidas) {
    prompt += `\n\n## FRASES PROHIBIDAS (nunca uses estas expresiones)\n${config.frasesProhibidas}`
  }

  prompt += `\n\n## REGLAS GENERALES\n- Si no sabes la respuesta a algo, no inventes. Di que un agente humano le ayudará.\n- Nunca compartas información falsa o que no esté en tus instrucciones.\n- Si el cliente está molesto o insatisfecho, muestra empatía antes de dar soluciones.\n- Sé conciso. Respuestas largas solo cuando sean necesarias.\n- Si el cliente pide hablar con una persona, escala inmediatamente sin resistencia.`

  if (config.promptPersonalizado) {
    prompt += `\n\n## INSTRUCCIONES ADICIONALES ESPECÍFICAS\n${config.promptPersonalizado}`
  }

  return prompt
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function ConfigBot({ sesionIdProp, embedded } = {}) {
  const params = useParams()
  const sesionId = sesionIdProp || params.sesionId
  const navigate = useNavigate()
  const [config, setConfig] = useState(null)
  const [sesion, setSesion] = useState(null)
  const [guardando, setGuardando] = useState(false)
  const [tab, setTab] = useState('identidad')
  const [verPrompt, setVerPrompt] = useState(false)
  const [promptGenerado, setPromptGenerado] = useState('')
  const [apiExpandida, setApiExpandida] = useState(null)
  const [epExpandido, setEpExpandido] = useState(null)
  const [testando, setTestando] = useState({})
  const [testResultados, setTestResultados] = useState({})
  // ── IA: catálogo de proveedores/modelos, prueba de conexión y playground ──
  const [catalogoIA, setCatalogoIA] = useState({ proveedores: [], modelos: [] })
  const [probandoIA, setProbandoIA] = useState(false)
  const [resultadoIA, setResultadoIA] = useState(null)
  const [promptReal, setPromptReal] = useState(null)
  const [cargandoPrompt, setCargandoPrompt] = useState(false)
  const [playMensaje, setPlayMensaje] = useState('')
  const [playCargando, setPlayCargando] = useState(false)
  const [playHistorial, setPlayHistorial] = useState([])
  const [subiendoMedia, setSubiendoMedia] = useState(false)

  useEffect(() => {
    api.get('/sesiones').then(r => { const arr = Array.isArray(r.data) ? r.data : r.data.sesiones; setSesion(arr.find(s => s.id === sesionId)) })
    api.get(`/sesiones/${sesionId}/config`).then(r => {
      const d = r.data
      setConfig({
        botActivo: false,
        modoOperacion: 'siempre',
        nombreBot: '',
        nombreNegocio: '',
        tono: 'amigable',
        objetivo: 'ventas',
        idioma: 'español',
        descripcionNegocio: '',
        productosServicios: '',
        procesosVenta: '',
        politicasNegocio: '',
        metodosPago: '',
        datosContacto: '',
        preguntasFrecuentes: '',
        restricciones: '',
        frasesProhibidas: '',
        promptPersonalizado: '',
        horario: '',
        mensajeBienvenida: '',
        mensajeFueraHorario: '',
        horarioEstructurado: { dias: [1,2,3,4,5], horaInicio: '08:00', horaFin: '18:00', timezone: 'America/La_Paz' },
        palabrasClaveEscalar: ['agente', 'humano', 'asesor', 'persona', 'reclamo'],
        maxMensajesSinEscalar: 10,
        modeloGemini: 'gemini-2.5-flash-lite',
        temperatura: 0.6,
        geminiApiKey: '',
        usarKnowledgeBase: true,
        // Comportamiento humano (anti-robot)
        simularEscritura: true,
        dividirMensajesLargos: true,
        agruparMensajes: true,
        ventanaAgrupacionSeg: 5,
        marcarLeido: true,
        entenderAudios: true,
        entenderImagenes: true,
        escalarSiNoSabe: true,
        antiAlucinacion: true,
        usarEmojis: true,
        longitudRespuesta: 'media',
        tratamiento: 'tu',
        apisExternas: d.apisExternas || [],
        proveedorIA: 'gemini',
        modeloIA: '',
        modeloFallback: '',
        apiKeyIA: '',
        baseUrlIA: '',
        permitirEnviarMedia: true,
        bibliotecaMedios: d.bibliotecaMedios || [],
        enlacesAutorizados: d.enlacesAutorizados || [],
        maxBurbujas: 4,
        ...d,
      })
    })
    api.get('/sesiones/ia/catalogo')
      .then(r => setCatalogoIA(r.data))
      .catch(() => setCatalogoIA({ proveedores: [], modelos: [] }))
  }, [sesionId])

  // ── Helpers para APIs externas ─────────────────────────────────────────
  const addApi = () => {
    const nueva = API_VACIA()
    upd('apisExternas', [...(config?.apisExternas || []), nueva])
    setApiExpandida(nueva.id)
  }

  const updApi = (apiId, campo, valor) => {
    upd('apisExternas', config.apisExternas.map(a => a.id === apiId ? { ...a, [campo]: valor } : a))
  }

  const removeApi = (apiId) => {
    upd('apisExternas', config.apisExternas.filter(a => a.id !== apiId))
    if (apiExpandida === apiId) setApiExpandida(null)
  }

  const addEndpoint = (apiId) => {
    const ep = ENDPOINT_VACIO()
    updApi(apiId, 'endpoints', [...(config.apisExternas.find(a => a.id === apiId)?.endpoints || []), ep])
    setEpExpandido(ep.id)
  }

  const updEndpoint = (apiId, epId, campo, valor) => {
    updApi(apiId, 'endpoints', config.apisExternas.find(a => a.id === apiId)?.endpoints.map(
      e => e.id === epId ? { ...e, [campo]: valor } : e
    ) || [])
  }

  const removeEndpoint = (apiId, epId) => {
    updApi(apiId, 'endpoints', config.apisExternas.find(a => a.id === apiId)?.endpoints.filter(e => e.id !== epId) || [])
  }

  const addParam = (apiId, epId) => {
    const ep = config.apisExternas.find(a => a.id === apiId)?.endpoints.find(e => e.id === epId)
    if (!ep) return
    updEndpoint(apiId, epId, 'parametros', [...(ep.parametros || []), PARAM_VACIO()])
  }

  const updParam = (apiId, epId, idx, campo, valor) => {
    const ep = config.apisExternas.find(a => a.id === apiId)?.endpoints.find(e => e.id === epId)
    if (!ep) return
    updEndpoint(apiId, epId, 'parametros', ep.parametros.map((p, i) => i === idx ? { ...p, [campo]: valor } : p))
  }

  const removeParam = (apiId, epId, idx) => {
    const ep = config.apisExternas.find(a => a.id === apiId)?.endpoints.find(e => e.id === epId)
    if (!ep) return
    updEndpoint(apiId, epId, 'parametros', ep.parametros.filter((_, i) => i !== idx))
  }

  const testEndpoint = async (apiObj, ep) => {
    const key = `${apiObj.id}_${ep.id}`
    setTestando(prev => ({ ...prev, [key]: true }))
    setTestResultados(prev => ({ ...prev, [key]: null }))
    try {
      const { data } = await api.post(`/sesiones/${sesionId}/config/test-api`, {
        api: { urlBase: apiObj.urlBase, authTipo: apiObj.authTipo, authHeader: apiObj.authHeader, authValor: apiObj.authValor, nombre: apiObj.nombre },
        endpoint: { path: ep.path, metodo: ep.metodo },
        parametros: {},
      })
      setTestResultados(prev => ({ ...prev, [key]: data }))
    } catch (e) {
      setTestResultados(prev => ({ ...prev, [key]: { ok: false, error: e.response?.data?.error || e.message } }))
    } finally {
      setTestando(prev => ({ ...prev, [key]: false }))
    }
  }

  const upd = useCallback((k, v) => setConfig(prev => ({ ...prev, [k]: v })), [])

  // ── Métodos de pago del bot (campo pagosBot, distinto del texto legacy metodosPago) ──
  const addMetodoPago = (tipo) => {
    const base = { id: Math.random().toString(36).slice(2, 9), tipo, label: tipo === 'qr' ? 'Pago con QR' : tipo === 'banco' ? 'Transferencia bancaria' : 'PayPal' }
    setConfig(prev => ({ ...prev, pagosBot: [...(Array.isArray(prev.pagosBot) ? prev.pagosBot : []), base] }))
  }
  const updMetodoPago = (id, campo, valor) => {
    setConfig(prev => ({ ...prev, pagosBot: (Array.isArray(prev.pagosBot) ? prev.pagosBot : []).map(m => m.id === id ? { ...m, [campo]: valor } : m) }))
  }
  const removeMetodoPago = (id) => {
    setConfig(prev => ({ ...prev, pagosBot: (Array.isArray(prev.pagosBot) ? prev.pagosBot : []).filter(m => m.id !== id) }))
  }
  const subirQR = async (id, file) => {
    if (!file) return
    const fd = new FormData()
    fd.append('file', file)
    try {
      const { data } = await api.post(`/sesiones/${sesionId}/config/upload-qr`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      updMetodoPago(id, 'imagenUrl', data.url)
      toast.success('QR subido ✅')
    } catch (e) { toast.error(e.response?.data?.error || 'No se pudo subir el QR') }
  }

  // ── IA: proveedor, modelo, prueba de conexión, prompt real y playground ──
  const modelosDelProveedor = (catalogoIA.modelos || []).filter(m => m.proveedor === (config?.proveedorIA || 'gemini'))
  const modeloActual = modelosDelProveedor.find(m => m.id === config?.modeloIA) || null
  const proveedorActual = (catalogoIA.proveedores || []).find(p => p.id === (config?.proveedorIA || 'gemini')) || null

  const cambiarProveedor = (proveedorId) => {
    const primero = (catalogoIA.modelos || []).find(m => m.proveedor === proveedorId && m.recomendado)
      || (catalogoIA.modelos || []).find(m => m.proveedor === proveedorId)
    setConfig(prev => ({
      ...prev,
      proveedorIA: proveedorId,
      modeloIA: primero?.id || '',
      modeloFallback: '',
      // La key es de cada proveedor: al cambiar hay que volver a pegarla.
      apiKeyIA: '',
    }))
    setResultadoIA(null)
  }

  const probarIA = async () => {
    setProbandoIA(true)
    setResultadoIA(null)
    try {
      const { data } = await api.post(`/sesiones/${sesionId}/config/test-ia`, {
        proveedorIA: config.proveedorIA,
        apiKeyIA: config.apiKeyIA,
        modeloIA: config.modeloIA,
        baseUrlIA: config.baseUrlIA,
      })
      setResultadoIA(data)
      if (data.ok) toast.success(`Conexión OK (${data.ms} ms)`)
    } catch (e) {
      const err = e.response?.data?.error || e.message
      setResultadoIA({ ok: false, error: err })
      toast.error('La prueba falló')
    } finally { setProbandoIA(false) }
  }

  const verPromptReal = async () => {
    setCargandoPrompt(true)
    try {
      const { data } = await api.get(`/sesiones/${sesionId}/config/prompt`)
      setPromptReal(data)
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo generar el prompt')
    } finally { setCargandoPrompt(false) }
  }

  const enviarPlayground = async () => {
    const texto = playMensaje.trim()
    if (!texto || playCargando) return
    setPlayCargando(true)
    setPlayMensaje('')
    setPlayHistorial(prev => [...prev, { rol: 'user', texto }])
    try {
      const { data } = await api.post(`/sesiones/${sesionId}/config/playground`, { mensaje: texto })
      setPlayHistorial(prev => [...prev, {
        rol: 'bot',
        partes: data.partes || [],
        llamadas: data.llamadas || [],
        escalar: data.escalar,
        modelo: data.modelo,
      }])
    } catch (e) {
      setPlayHistorial(prev => [...prev, { rol: 'error', texto: e.response?.data?.error || e.message }])
    } finally { setPlayCargando(false) }
  }

  // ── Biblioteca de medios que el bot puede enviar ──
  const addMedia = (item) => setConfig(prev => ({
    ...prev, bibliotecaMedios: [...(prev.bibliotecaMedios || []), item],
  }))
  const updMedia = (id, campo, valor) => setConfig(prev => ({
    ...prev, bibliotecaMedios: (prev.bibliotecaMedios || []).map(m => m.id === id ? { ...m, [campo]: valor } : m),
  }))
  const removeMedia = (id) => setConfig(prev => ({
    ...prev, bibliotecaMedios: (prev.bibliotecaMedios || []).filter(m => m.id !== id),
  }))

  const subirMedia = async (file) => {
    if (!file) return
    setSubiendoMedia(true)
    const fd = new FormData()
    fd.append('file', file)
    try {
      const { data } = await api.post(`/sesiones/${sesionId}/config/media`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      const base = (file.name || 'archivo').replace(/\.[^.]+$/, '')
      addMedia({
        id: uid(),
        clave: base.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 30) || `archivo_${uid()}`,
        tipo: data.tipo, nombre: data.nombre, descripcion: '',
        url: data.url, mimetype: data.mimetype, caption: '', activo: true,
      })
      toast.success('Archivo subido ✅ — ponle una descripción para que el bot sepa cuándo usarlo')
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo subir el archivo')
    } finally { setSubiendoMedia(false) }
  }

  // ── Enlaces que el bot tiene permitido compartir ──
  const addEnlace = () => setConfig(prev => ({
    ...prev, enlacesAutorizados: [...(prev.enlacesAutorizados || []), { id: uid(), titulo: '', url: '', descripcion: '', activo: true }],
  }))
  const updEnlace = (id, campo, valor) => setConfig(prev => ({
    ...prev, enlacesAutorizados: (prev.enlacesAutorizados || []).map(e => e.id === id ? { ...e, [campo]: valor } : e),
  }))
  const removeEnlace = (id) => setConfig(prev => ({
    ...prev, enlacesAutorizados: (prev.enlacesAutorizados || []).filter(e => e.id !== id),
  }))

  const aplicarPlantilla = (plantilla) => {
    setConfig(prev => ({ ...prev, ...plantilla.campos }))
    toast.success(`✅ Plantilla "${plantilla.nombre}" aplicada`)
    setTab('identidad')
  }

  const generarYVerPrompt = () => {
    const p = generarPromptMaestro(config)
    setPromptGenerado(p)
    setVerPrompt(true)
  }

  const guardar = async () => {
    setGuardando(true)
    const promptFinal = generarPromptMaestro(config)
    try {
      const { data } = await api.put(`/sesiones/${sesionId}/config`, { ...config, promptGenerado: promptFinal })
      setConfig(data)
      toast.success('✅ Configuración guardada')
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al guardar')
    } finally { setGuardando(false) }
  }

  if (!config) return <div className="loading-fullscreen"><div className="spinner" /></div>

  const TABS = [
    { id: 'plantillas', label: '⚡ Plantillas' },
    { id: 'identidad', label: '🤖 Identidad' },
    { id: 'negocio', label: '🏢 Negocio' },
    { id: 'comunicacion', label: '💬 Comunicación' },
    { id: 'horario', label: '⏰ Horario' },
    { id: 'escalamiento', label: '🚨 Escalamiento' },
    { id: 'ia', label: '🧠 IA' },
    { id: 'medios', label: '📎 Archivos y Enlaces', badge: ((config.bibliotecaMedios || []).filter(m => m.activo !== false).length + (config.enlacesAutorizados || []).filter(e => e.activo !== false).length) || null },
    { id: 'pedidos', label: '🛒 Pedidos y Pagos', badge: config.tomarPedidos ? ((Array.isArray(config.pagosBot) ? config.pagosBot : []).length || null) : null },
    { id: 'apis', label: '🔌 APIs Externas', badge: (config.apisExternas || []).filter(a => a.activo).length || null },
  ]

  const inner = (
    <>
      {/* Header — solo en página completa */}
      {!embedded && (
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <button className="btn btn-ghost" onClick={() => navigate('/sesiones')} style={{ marginBottom: 6 }}>← Volver</button>
            <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>⚙️ Configurar Bot IA</h1>
            <p className="text-muted" style={{ margin: '4px 0 0' }}>
              {sesion?.nombre} · Cuanto más completes, mejor responderá la IA
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn" onClick={generarYVerPrompt}>👁 Ver prompt generado</button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '8px 14px' }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Bot activo</span>
              <div className={`toggle ${config.botActivo ? 'on' : ''}`} onClick={() => upd('botActivo', !config.botActivo)} />
            </div>
            <button className="btn btn-primary" onClick={guardar} disabled={guardando}>
              {guardando ? 'Guardando...' : '💾 Guardar'}
            </button>
          </div>
        </div>
      )}

      {/* Barra compacta de acciones — solo en modo embebido */}
      {embedded && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '8px 14px' }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Bot activo</span>
            <div className={`toggle ${config.botActivo ? 'on' : ''}`} onClick={() => upd('botActivo', !config.botActivo)} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={generarYVerPrompt}>👁 Ver prompt</button>
            <button className="btn btn-primary" onClick={guardar} disabled={guardando}>
              {guardando ? 'Guardando...' : '💾 Guardar bot'}
            </button>
          </div>
        </div>
      )}

      {/* Progress bar */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Completitud del bot</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)' }}>{calcularCompletitud(config)}%</span>
        </div>
        <div style={{ background: 'var(--border)', borderRadius: 99, height: 6 }}>
          <div style={{ width: `${calcularCompletitud(config)}%`, background: 'linear-gradient(90deg, #25D366, #128C7E)', borderRadius: 99, height: 6, transition: 'width 0.4s' }} />
        </div>
        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          {calcularCompletitud(config) < 40 ? '⚠️ Bot básico — agrega más info para mejores respuestas'
            : calcularCompletitud(config) < 70 ? '🟡 Bot moderado — completa más secciones para optimizar'
            : calcularCompletitud(config) < 90 ? '🟢 Bot bien configurado'
            : '⭐ Bot completamente configurado — máxima calidad de respuestas'}
        </p>
      </div>

      {/* Tabs */}
      <div className="tabs-h-scroll" style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 4 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{
              padding: '8px 14px', borderRadius: '8px 8px 0 0', border: 'none', cursor: 'pointer',
              fontWeight: tab === t.id ? 700 : 500, fontSize: 13,
              background: tab === t.id ? 'var(--primary)' : 'transparent',
              color: tab === t.id ? 'white' : 'var(--text-soft)',
              transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 6,
            }}>
            {t.label}
            {t.badge ? <span style={{ background: '#25D366', color: 'white', borderRadius: 99, fontSize: 11, fontWeight: 800, padding: '1px 7px' }}>{t.badge}</span> : null}
          </button>
        ))}
      </div>

      {/* ── Tab: Plantillas ── */}
      {tab === 'plantillas' && (
        <div>
          <div className="card" style={{ marginBottom: 16, background: 'linear-gradient(135deg, #f0fdf4, #ecfdf5)', borderColor: '#a7f3d0' }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700 }}>⚡ Comienza con una plantilla profesional</h3>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-soft)' }}>
              Elige la plantilla más cercana a tu negocio. Luego personaliza los detalles. Esto configurará automáticamente el tono, objetivo, procesos y restricciones.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
            {PLANTILLAS.map(p => (
              <div key={p.id} className="card" style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                onClick={() => aplicarPlantilla(p)}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>{p.icon}</div>
                <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700 }}>{p.nombre}</h3>
                <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--text-soft)' }}>
                  Tono {p.campos.tono} · Objetivo: {OBJETIVOS.find(o => o.val === p.campos.objetivo)?.label}
                </p>
                <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', fontSize: 13 }}>
                  Aplicar plantilla →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Tab: Identidad ── */}
      {tab === 'identidad' && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">🤖 Identidad del asistente</h3>
            <p className="card-subtitle">Cómo se presenta el bot ante los clientes</p>

            <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Nombre del bot *</label>
                <input className="input" value={config.nombreBot || ''} onChange={e => upd('nombreBot', e.target.value)} placeholder="Ej: Sofia, Max, Asistente" />
                <div className="form-hint">El bot se presentará con este nombre</div>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Nombre del negocio *</label>
                <input className="input" value={config.nombreNegocio || ''} onChange={e => upd('nombreNegocio', e.target.value)} placeholder="Ej: Tienda Moda Norte" />
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">🎯 Tono y objetivo</h3>
            <p className="card-subtitle">Define cómo habla el bot y qué quiere lograr en cada conversación</p>

            <label className="form-label">Tono de comunicación *</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10, marginBottom: 20 }}>
              {TONOS.map(t => (
                <div key={t.val} onClick={() => upd('tono', t.val)}
                  style={{
                    padding: '12px 14px', borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s',
                    border: `2px solid ${config.tono === t.val ? 'var(--primary)' : 'var(--border)'}`,
                    background: config.tono === t.val ? 'var(--bg-active)' : 'var(--bg-card)',
                  }}>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{t.label}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t.desc}</div>
                </div>
              ))}
            </div>

            <label className="form-label">Objetivo principal del bot *</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8, marginBottom: 20 }}>
              {OBJETIVOS.map(o => (
                <div key={o.val} onClick={() => upd('objetivo', o.val)}
                  style={{
                    padding: '10px 14px', borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s',
                    border: `2px solid ${config.objetivo === o.val ? 'var(--primary)' : 'var(--border)'}`,
                    background: config.objetivo === o.val ? 'var(--bg-active)' : 'var(--bg-card)',
                    fontSize: 13, fontWeight: 600, textAlign: 'center',
                  }}>
                  {o.label}
                </div>
              ))}
            </div>

            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Idioma de respuesta</label>
              <select className="select" value={config.idioma} onChange={e => upd('idioma', e.target.value)}>
                {IDIOMAS.map(i => <option key={i.val} value={i.val}>{i.label}</option>)}
              </select>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">💬 Mensajes automáticos</h3>

            <div className="form-group">
              <label className="form-label">Mensaje de bienvenida</label>
              <textarea className="textarea" rows={3} value={config.mensajeBienvenida || ''} onChange={e => upd('mensajeBienvenida', e.target.value)}
                placeholder="¡Hola! 👋 Soy Sofia, la asistente virtual de [Negocio]. Estoy aquí para ayudarte. ¿En qué puedo servirte hoy?" />
              <div className="form-hint">Se envía cuando el cliente escribe por primera vez</div>
            </div>

            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Modo de operación</label>
              <select className="select" value={config.modoOperacion} onChange={e => upd('modoOperacion', e.target.value)}>
                <option value="siempre">Siempre activo (24/7)</option>
                <option value="fuera_horario">Solo fuera del horario de atención</option>
                <option value="solo_manual">Solo cuando lo active manualmente</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Negocio ── */}
      {tab === 'negocio' && (
        <div>
          <div style={{ background: '#fefce8', border: '1px solid #fde047', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#713f12' }}>
            💡 <strong>Tip:</strong> Cuanta más información pongas aquí, más precisa y útil será la IA. No tengas miedo de ser detallado.
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">🏢 Descripción del negocio *</h3>
            <p className="card-subtitle">Contexto general: qué hace tu empresa, dónde opera, qué la hace diferente</p>
            <textarea className="textarea" rows={4} value={config.descripcionNegocio || ''} onChange={e => upd('descripcionNegocio', e.target.value)}
              placeholder="Ej: Somos una tienda de ropa deportiva fundada en 2018 en Bogotá. Nos especializamos en marcas premium para entrenamiento y running. Tenemos tienda física y envíos a todo Colombia. Nos diferenciamos por la asesoría personalizada y garantía extendida de 1 año." />
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">🛍️ Productos / Servicios con precios *</h3>
            <p className="card-subtitle">Lista lo que vendes con precios aproximados. La IA usará esto para cotizar</p>
            <textarea className="textarea" rows={8} value={config.productosServicios || ''} onChange={e => upd('productosServicios', e.target.value)}
              placeholder={`Ej:
ROPA DEPORTIVA
- Camisetas técnicas: $25,000 - $45,000
- Shorts deportivos: $35,000 - $55,000
- Licras: $40,000 - $70,000

CALZADO
- Zapatillas running: $150,000 - $350,000
- Tenis entrenamiento: $120,000 - $280,000

ACCESORIOS
- Guantes: $20,000 - $40,000
- Medias deportivas: $15,000 - $25,000

Promoción actual: 2x1 en medias hasta fin de mes`} />
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">🔄 Proceso de atención paso a paso *</h3>
            <p className="card-subtitle">Dile al bot exactamente cómo debe guiar al cliente de inicio a cierre</p>
            <textarea className="textarea" rows={7} value={config.procesosVenta || ''} onChange={e => upd('procesosVenta', e.target.value)}
              placeholder={`Ej:
1. Saluda al cliente y preséntate
2. Pregunta qué tipo de deporte o actividad practica
3. Según el deporte, presenta las opciones más adecuadas
4. Pregunta talla y color de preferencia
5. Informa el precio y disponibilidad
6. Si confirma, pide: nombre completo, dirección de envío, ciudad
7. Informa el total con envío ($8,000 envío estándar, gratis sobre $200,000)
8. Comparte los datos para pago y confirma el pedido`} />
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">📋 Políticas del negocio</h3>
            <p className="card-subtitle">Devoluciones, garantías, envíos, tiempos de entrega</p>
            <textarea className="textarea" rows={5} value={config.politicasNegocio || ''} onChange={e => upd('politicasNegocio', e.target.value)}
              placeholder={`Ej:
ENVÍOS: 2-4 días hábiles en ciudades principales. 4-7 días en municipios.
DEVOLUCIONES: Hasta 15 días con producto sin uso y empaque original.
GARANTÍA: 6 meses contra defectos de fabricación.
CAMBIOS: Se permiten cambios de talla sin costo adicional en los primeros 15 días.`} />
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">💳 Métodos de pago</h3>
            <textarea className="textarea" rows={3} value={config.metodosPago || ''} onChange={e => upd('metodosPago', e.target.value)}
              placeholder="Ej: Nequi (300-000-0000), Bancolombia (cta 123-456789-00), efectivo contraentrega, tarjeta de crédito en tienda física." />
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">📞 Datos de contacto adicionales</h3>
            <p className="card-subtitle">Teléfono fijo, dirección, web, redes sociales</p>
            <textarea className="textarea" rows={4} value={config.datosContacto || ''} onChange={e => upd('datosContacto', e.target.value)}
              placeholder={`Ej:
Tienda física: Calle 72 #15-20, Bogotá. Lunes a Sábado 9am-7pm.
Instagram: @mideporte_col
Web: www.mideporte.com
Teléfono fijo: (601) 234-5678`} />
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">❓ Preguntas frecuentes (Q&A)</h3>
            <p className="card-subtitle">Las preguntas más comunes de tus clientes y sus respuestas exactas</p>
            <textarea className="textarea" rows={8} value={config.preguntasFrecuentes || ''} onChange={e => upd('preguntasFrecuentes', e.target.value)}
              placeholder={`Ej:
P: ¿Tienen tallas grandes (XL, XXL)?
R: Sí, manejamos tallas S, M, L, XL y XXL en la mayoría de nuestros productos.

P: ¿Hacen envíos internacionales?
R: Por el momento solo enviamos a Colombia.

P: ¿Los precios incluyen IVA?
R: Sí, todos los precios mostrados incluyen IVA.

P: ¿Cuánto demora el envío a Medellín?
R: 2-3 días hábiles a Medellín.`} />
          </div>
        </div>
      )}

      {/* ── Tab: Comunicación ── */}
      {tab === 'comunicacion' && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">🚫 Restricciones (qué NO debe hacer el bot)</h3>
            <p className="card-subtitle">Límites claros para evitar respuestas incorrectas o comprometedoras</p>
            <textarea className="textarea" rows={6} value={config.restricciones || ''} onChange={e => upd('restricciones', e.target.value)}
              placeholder={`Ej:
- No hagas descuentos sin autorización de un administrador
- No confirmes stock de productos sin verificar en el catálogo actualizado
- No des información de proveedores o costos internos
- No hagas promesas de entrega con fechas exactas que no puedas garantizar
- No compartas datos personales de otros clientes
- No hagas comentarios negativos sobre la competencia`} />
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">🚷 Frases prohibidas</h3>
            <p className="card-subtitle">Expresiones que el bot nunca debe decir (una por línea)</p>
            <textarea className="textarea" rows={5} value={config.frasesProhibidas || ''} onChange={e => upd('frasesProhibidas', e.target.value)}
              placeholder={`Ej:
No lo sé (reemplaza con: déjame verificarlo con nuestro equipo)
No podemos hacer eso
Eso no es posible
No es mi problema
Espere`} />
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">✏️ Instrucciones adicionales personalizadas</h3>
            <p className="card-subtitle">Cualquier instrucción específica que no encaje en las secciones anteriores</p>
            <textarea className="textarea" rows={5} value={config.promptPersonalizado || ''} onChange={e => upd('promptPersonalizado', e.target.value)}
              placeholder={`Ej:
- Si el cliente es de Venezuela o Ecuador, informa que el envío tiene un costo adicional.
- Cuando alguien pregunta por mayoristas, pídele que deje su nombre y empresa para que lo contacte el equipo comercial.
- Siempre ofrece el producto más vendido del mes como primera recomendación.`} />
          </div>
        </div>
      )}

      {/* ── Tab: Horario ── */}
      {tab === 'horario' && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Horario de atención (texto para mostrar al cliente)</label>
              <input className="input" value={config.horario || ''} onChange={e => upd('horario', e.target.value)} placeholder="Lunes a Viernes 8am - 6pm, Sábados 9am - 2pm" />
              <div className="form-hint">El bot usará este texto cuando le pregunten el horario</div>
            </div>

            {config.modoOperacion === 'fuera_horario' && (
              <div style={{ background: 'var(--bg)', borderRadius: 10, padding: 16 }}>
                <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700 }}>⏰ Horario real de activación del bot</h4>

                <label className="form-label">Días activos</label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                  {[{l:'Dom',v:0},{l:'Lun',v:1},{l:'Mar',v:2},{l:'Mié',v:3},{l:'Jue',v:4},{l:'Vie',v:5},{l:'Sáb',v:6}].map(d => {
                    const dias = config.horarioEstructurado?.dias || [1,2,3,4,5]
                    const activo = dias.includes(d.v)
                    return (
                      <div key={d.v} onClick={() => {
                        const nuevos = activo ? dias.filter(x => x !== d.v) : [...dias, d.v].sort()
                        upd('horarioEstructurado', { ...(config.horarioEstructurado||{}), dias: nuevos })
                      }} style={{
                        padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13,
                        background: activo ? 'var(--primary)' : 'var(--border)',
                        color: activo ? 'white' : 'var(--text-muted)',
                      }}>{d.l}</div>
                    )
                  })}
                </div>

                <div className="api-auth-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <div>
                    <label className="form-label" style={{ fontSize: 12 }}>Hora inicio</label>
                    <input type="time" className="input" value={config.horarioEstructurado?.horaInicio || '08:00'}
                      onChange={e => upd('horarioEstructurado', { ...(config.horarioEstructurado||{}), horaInicio: e.target.value })} />
                  </div>
                  <div>
                    <label className="form-label" style={{ fontSize: 12 }}>Hora fin</label>
                    <input type="time" className="input" value={config.horarioEstructurado?.horaFin || '18:00'}
                      onChange={e => upd('horarioEstructurado', { ...(config.horarioEstructurado||{}), horaFin: e.target.value })} />
                  </div>
                  <div>
                    <label className="form-label" style={{ fontSize: 12 }}>Zona horaria</label>
                    <select className="select" value={config.horarioEstructurado?.timezone || 'America/La_Paz'}
                      onChange={e => upd('horarioEstructurado', { ...(config.horarioEstructurado||{}), timezone: e.target.value })}>
                      <option value="America/Bogota">Colombia (UTC-5)</option>
                      <option value="America/Lima">Perú (UTC-5)</option>
                      <option value="America/Guayaquil">Ecuador (UTC-5)</option>
                      <option value="America/La_Paz">Bolivia (UTC-4)</option>
                      <option value="America/Santiago">Chile (UTC-3/-4)</option>
                      <option value="America/Argentina/Buenos_Aires">Argentina (UTC-3)</option>
                      <option value="America/Sao_Paulo">Brasil (UTC-3)</option>
                      <option value="America/Caracas">Venezuela (UTC-4)</option>
                      <option value="America/Mexico_City">México (UTC-6)</option>
                      <option value="America/New_York">Nueva York (UTC-5/-4)</option>
                      <option value="Europe/Madrid">España (UTC+1/+2)</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">💤 Mensaje fuera de horario</h3>
            <textarea className="textarea" rows={3} value={config.mensajeFueraHorario || ''} onChange={e => upd('mensajeFueraHorario', e.target.value)}
              placeholder="¡Hola! 😊 Gracias por escribirnos. Nuestro horario de atención es Lunes a Viernes de 8am a 6pm. Tu mensaje es importante para nosotros, ¡te responderemos en cuanto abramos!" />
            <div className="form-hint">Se envía cuando el cliente escribe fuera del horario configurado</div>
          </div>
        </div>
      )}

      {/* ── Tab: Escalamiento ── */}
      {tab === 'escalamiento' && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">🚨 Palabras clave para escalar a humano</h3>
            <p className="card-subtitle">Si el cliente dice alguna de estas palabras, el bot transfiere el chat a un agente</p>
            <input className="input" value={(config.palabrasClaveEscalar || []).join(', ')}
              onChange={e => upd('palabrasClaveEscalar', e.target.value.split(',').map(p => p.trim()).filter(Boolean))}
              placeholder="agente, humano, asesor, persona real, reclamo, queja, gerente" />
            <div className="form-hint">Separa con comas · Recomendadas: agente, humano, asesor, reclamo, queja, cancelar, devolver</div>
            <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(config.palabrasClaveEscalar || []).map(p => (
                <span key={p} style={{ background: '#fee2e2', color: '#991b1b', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                  {p}
                </span>
              ))}
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">🔢 Límite de mensajes sin escalar</h3>
            <p className="card-subtitle">Si el bot lleva X mensajes seguidos sin resolver al cliente, escala automáticamente</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <input type="range" min="3" max="20" step="1" value={config.maxMensajesSinEscalar || 10}
                onChange={e => upd('maxMensajesSinEscalar', parseInt(e.target.value))} style={{ flex: 1 }} />
              <span style={{ fontWeight: 800, fontSize: 20, color: 'var(--primary)', minWidth: 40 }}>{config.maxMensajesSinEscalar || 10}</span>
            </div>
            <div className="form-hint">Mensajes de ida y vuelta. Recomendado: 8-12</div>
          </div>

          <div className="card" style={{ background: '#f0fdf4', borderColor: '#a7f3d0', marginBottom: 16 }}>
            <h3 className="card-title">💬 Mensaje de escalamiento</h3>
            <p className="card-subtitle">Lo que el bot dice cuando va a transferir al agente humano</p>
            <textarea className="textarea" rows={2}
              value={config.mensajeEscalamiento || ''}
              onChange={e => upd('mensajeEscalamiento', e.target.value)}
              placeholder="¡Claro! Voy a conectarte con uno de nuestros agentes especializados. Por favor espera un momento, te atenderán muy pronto. 😊" />
          </div>
        </div>
      )}

      {/* ── Tab: IA ── */}
      {tab === 'ia' && (
        <div>
          {/* ── Proveedor de IA ── */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">🔌 Proveedor de IA</h3>
            <div className="form-hint" style={{ marginBottom: 14 }}>
              Es el servicio que le da inteligencia a este número. Cada empresa usa su propia cuenta y su propia clave.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 10 }}>
              {(catalogoIA.proveedores || []).map(p => {
                const activo = (config.proveedorIA || 'gemini') === p.id
                return (
                  <div key={p.id} onClick={() => cambiarProveedor(p.id)}
                    style={{
                      padding: 12, borderRadius: 12, cursor: 'pointer',
                      border: activo ? '2px solid var(--primary)' : '1px solid var(--border)',
                      background: activo ? 'var(--bg-active)' : 'transparent',
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: 13 }}>{p.nombre}</span>
                      {p.recomendado && <span style={{ background: '#10b98122', color: '#10b981', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 20 }}>Recomendado</span>}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.45 }}>{p.descripcion}</div>
                  </div>
                )
              })}
            </div>

            {/* API key del proveedor */}
            <div style={{ marginTop: 16 }}>
              <label className="form-label">
                🔑 API key de {proveedorActual?.nombre || 'el proveedor'}
                {config.apiKeyIASet && <span style={{ marginLeft: 8, color: '#10b981', fontSize: 11, fontWeight: 700 }}>✓ Guardada</span>}
              </label>
              <input className="form-input" type="password" autoComplete="new-password"
                placeholder={config.apiKeyIASet ? '•••••••• (déjalo así para conservar la actual)' : (proveedorActual?.formatoKey || 'Pega aquí tu API key')}
                value={config.apiKeyIA || ''} onChange={e => upd('apiKeyIA', e.target.value)} />
              <div className="form-hint" style={{ marginTop: 6 }}>
                Se guarda cifrada: nadie puede volver a leerla desde el panel.
                {proveedorActual?.urlApiKey && (
                  <> ¿No tienes una? <a href={proveedorActual.urlApiKey} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)', fontWeight: 600 }}>Créala aquí →</a></>
                )}
              </div>
            </div>

            {config.proveedorIA === 'personalizado' && (
              <div style={{ marginTop: 12 }}>
                <label className="form-label">🌐 URL base del servicio</label>
                <input className="form-input" placeholder="https://mi-servidor.com/v1"
                  value={config.baseUrlIA || ''} onChange={e => upd('baseUrlIA', e.target.value)} />
                <div className="form-hint" style={{ marginTop: 6 }}>Debe ser compatible con la API de OpenAI (/chat/completions).</div>
              </div>
            )}
          </div>

          {/* ── Modelo ── */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">🧠 Modelo que responderá los mensajes</h3>
            {config.proveedorIA === 'personalizado' ? (
              <>
                <label className="form-label">Nombre del modelo</label>
                <input className="form-input" placeholder="ej: mi-modelo-v1"
                  value={config.modeloIA || ''} onChange={e => upd('modeloIA', e.target.value)} />
              </>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {modelosDelProveedor.map(m => {
                  const activo = config.modeloIA === m.id
                  return (
                    <div key={m.id} onClick={() => upd('modeloIA', m.id)}
                      style={{
                        padding: 12, borderRadius: 12, cursor: 'pointer',
                        border: activo ? '2px solid var(--primary)' : '1px solid var(--border)',
                        background: activo ? 'var(--bg-active)' : 'transparent',
                      }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                        <span style={{ fontWeight: 700, fontSize: 14 }}>{m.nombre}</span>
                        {m.etiqueta && <span style={{ background: '#10b98122', color: '#10b981', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 20 }}>{m.etiqueta}</span>}
                        {m.audio && <span title="Entiende notas de voz" style={{ fontSize: 11 }}>🎤</span>}
                        {m.vision && <span title="Entiende imágenes" style={{ fontSize: 11 }}>🖼️</span>}
                        {m.tools && <span title="Sabe usar funciones (catálogo, pedidos, archivos)" style={{ fontSize: 11 }}>🔧</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.45 }}>{m.descripcion}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                        💵 ${m.precioEntrada}/M entrada · ${m.precioSalida}/M salida
                        {' · '}≈ ${((m.precioEntrada * 4000 + m.precioSalida * 250) / 1e6 * 1000).toFixed(2)} por cada 1.000 respuestas
                      </div>
                      {m.advertencia && (
                        <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 6, fontWeight: 600 }}>⚠️ {m.advertencia}</div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            {/* Modelo de respaldo */}
            {config.proveedorIA !== 'personalizado' && modelosDelProveedor.length > 1 && (
              <div style={{ marginTop: 14 }}>
                <label className="form-label">🛟 Modelo de respaldo (opcional)</label>
                <select className="form-input" value={config.modeloFallback || ''} onChange={e => upd('modeloFallback', e.target.value)}>
                  <option value="">Sin respaldo</option>
                  {modelosDelProveedor.filter(m => m.id !== config.modeloIA).map(m => (
                    <option key={m.id} value={m.id}>{m.nombre}</option>
                  ))}
                </select>
                <div className="form-hint" style={{ marginTop: 6 }}>
                  Si el modelo principal está saturado o falla, se usa este automáticamente en vez de dejar al cliente sin respuesta.
                </div>
              </div>
            )}

            {/* Aviso de capacidades faltantes */}
            {modeloActual && (!modeloActual.audio || !modeloActual.vision) && (
              <div style={{ marginTop: 14, padding: 12, borderRadius: 10, background: '#f59e0b18', border: '1px solid #f59e0b55' }}>
                <div style={{ fontSize: 12, color: '#b45309', lineHeight: 1.5 }}>
                  ⚠️ <strong>{modeloActual.nombre}</strong> no entiende{' '}
                  {[!modeloActual.audio && 'notas de voz', !modeloActual.vision && 'imágenes'].filter(Boolean).join(' ni ')}.
                  {' '}Cuando un cliente envíe eso, el bot se disculpará y le pedirá que lo escriba. Si necesitas esas funciones, elige un modelo con 🎤 y 🖼️.
                </div>
              </div>
            )}

            {/* Probar conexión */}
            <div style={{ marginTop: 16, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="btn btn-secondary" onClick={probarIA} disabled={probandoIA}>
                {probandoIA ? '⏳ Probando…' : '🧪 Probar conexión'}
              </button>
              <span className="form-hint" style={{ margin: 0 }}>Hace una llamada real para confirmar que la clave y el modelo funcionan.</span>
            </div>
            {resultadoIA && (
              <div style={{
                marginTop: 12, padding: 12, borderRadius: 10, fontSize: 12, lineHeight: 1.5,
                background: resultadoIA.ok ? '#10b98118' : '#ef444418',
                border: `1px solid ${resultadoIA.ok ? '#10b98155' : '#ef444455'}`,
                color: resultadoIA.ok ? '#047857' : '#b91c1c',
              }}>
                {resultadoIA.ok
                  ? <>✅ <strong>Funciona.</strong> {resultadoIA.modelo} respondió en {resultadoIA.ms} ms: “{resultadoIA.respuesta}”</>
                  : <>❌ <strong>Falló:</strong> {resultadoIA.error}</>}
              </div>
            )}
          </div>

          {/* ── Prompt real que recibe el modelo ── */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">📄 Instrucciones exactas que recibe el bot</h3>
            <div className="form-hint" style={{ marginBottom: 12 }}>
              Esto es literalmente todo lo que el modelo "sabe" antes de responder: tu identidad, reglas de estilo, anti-alucinación, horario, base de conocimiento, funciones y enlaces permitidos.
            </div>
            <button className="btn btn-secondary" onClick={verPromptReal} disabled={cargandoPrompt}>
              {cargandoPrompt ? '⏳ Generando…' : '👁️ Ver instrucciones completas'}
            </button>
            {promptReal && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
                  {promptReal.caracteres.toLocaleString()} caracteres · ~{promptReal.tokensAprox.toLocaleString()} tokens por mensaje
                </div>
                <pre style={{
                  maxHeight: 420, overflow: 'auto', fontSize: 11, lineHeight: 1.55, whiteSpace: 'pre-wrap',
                  background: 'var(--bg)', padding: 12, borderRadius: 10, border: '1px solid var(--border)',
                }}>{promptReal.prompt}</pre>
              </div>
            )}
          </div>

          {/* ── Playground ── */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">💬 Probar el bot aquí mismo</h3>
            <div className="form-hint" style={{ marginBottom: 12 }}>
              Conversa con el bot sin usar WhatsApp ni tocar ningún chat real. Se muestra qué funciones llamó, para verificar que consulta datos en vez de inventarlos.
              <br />Guarda los cambios antes de probar: se usa la configuración ya guardada.
            </div>

            {playHistorial.length > 0 && (
              <div style={{ maxHeight: 360, overflowY: 'auto', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {playHistorial.map((m, i) => (
                  <div key={i} style={{ alignSelf: m.rol === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                    {m.rol === 'user' && (
                      <div style={{ background: 'var(--primary)', color: 'white', padding: '8px 12px', borderRadius: 12, fontSize: 13 }}>{m.texto}</div>
                    )}
                    {m.rol === 'error' && (
                      <div style={{ background: '#ef444418', color: '#b91c1c', padding: '8px 12px', borderRadius: 12, fontSize: 12 }}>❌ {m.texto}</div>
                    )}
                    {m.rol === 'bot' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {(m.llamadas || []).map((l, j) => (
                          <div key={j} style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace', background: 'var(--bg)', padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)' }}>
                            🔧 {l.funcion}({JSON.stringify(l.argumentos || {})}) · {l.ms} ms
                          </div>
                        ))}
                        {(m.partes || []).map((p, j) => (
                          <div key={j} style={{ background: 'var(--bg-active)', padding: '8px 12px', borderRadius: 12, fontSize: 13, whiteSpace: 'pre-wrap' }}>{p}</div>
                        ))}
                        {m.escalar && <div style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600 }}>🚨 El bot derivó la conversación a un humano</div>}
                        {!m.llamadas?.length && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Sin llamadas a funciones · {m.modelo}</div>}
                      </div>
                    )}
                  </div>
                ))}
                {playCargando && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>⏳ El bot está pensando…</div>}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <input className="form-input" placeholder="Escribe como si fueras un cliente… ej: hola, ¿qué venden?"
                value={playMensaje} onChange={e => setPlayMensaje(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') enviarPlayground() }} />
              <button className="btn btn-primary" onClick={enviarPlayground} disabled={playCargando || !playMensaje.trim()}>Enviar</button>
              {playHistorial.length > 0 && (
                <button className="btn btn-secondary" onClick={() => setPlayHistorial([])}>Limpiar</button>
              )}
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">🌡️ Creatividad (temperatura): {config.temperatura}</h3>
            <input type="range" min="0" max="1" step="0.1" value={config.temperatura}
              onChange={e => upd('temperatura', parseFloat(e.target.value))} style={{ width: '100%', marginBottom: 8 }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-muted)' }}>
              <span>0 — Respuestas exactas y predecibles</span>
              <span>1 — Más creativo y variado</span>
            </div>
            <div className="form-hint" style={{ marginTop: 8 }}>
              {config.temperatura <= 0.3 ? '📌 Muy preciso — Ideal para precios, datos técnicos, políticas exactas'
                : config.temperatura <= 0.6 ? '✅ Equilibrado — Recomendado para atención al cliente general'
                : config.temperatura <= 0.8 ? '✨ Creativo — Bueno para ventas y conversaciones fluidas'
                : '🎨 Muy creativo — Respuestas más variadas, puede desviarse del tema'}
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="flex justify-between items-center" style={{ marginBottom: 14 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>📚 Usar base de conocimientos</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Incluye los documentos que subiste en "Base de conocimiento" al responder</div>
              </div>
              <div className={`toggle ${config.usarKnowledgeBase ? 'on' : ''}`} onClick={() => upd('usarKnowledgeBase', !config.usarKnowledgeBase)} />
            </div>
          </div>

          {/* Comportamiento humano */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">🧑 Comportamiento humano (anti-robot)</h3>
            <div className="form-hint" style={{ marginBottom: 14 }}>Hace que el bot se sienta como una persona real atendiendo.</div>
            {[
              { k: 'simularEscritura',      t: '✍️ Mostrar "escribiendo…"', d: 'Muestra el indicador de escritura y responde con un retraso natural según el largo del mensaje' },
              { k: 'agruparMensajes',       t: '💬 Agrupar mensajes seguidos', d: 'Si el cliente envía varios mensajes rápidos, espera y responde una sola vez de forma coherente' },
              { k: 'dividirMensajesLargos', t: '📩 Dividir respuestas largas', d: 'Parte las respuestas extensas en varias burbujas, como escribiría una persona' },
              { k: 'marcarLeido',           t: '✓✓ Marcar como leído', d: 'Muestra el doble check azul cuando el bot lee el mensaje' },
            ].map(o => (
              <div key={o.k} className="flex justify-between items-center" style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ paddingRight: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{o.t}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{o.d}</div>
                </div>
                <div className={`toggle ${config[o.k] ? 'on' : ''}`} onClick={() => upd(o.k, !config[o.k])} />
              </div>
            ))}
            {config.agruparMensajes && (
              <div style={{ marginTop: 14 }}>
                <label className="form-label">⏱ Tiempo de espera para agrupar: {config.ventanaAgrupacionSeg || 5}s</label>
                <input type="range" min="2" max="12" step="1" value={config.ventanaAgrupacionSeg || 5}
                  onChange={e => upd('ventanaAgrupacionSeg', parseInt(e.target.value))} style={{ width: '100%' }} />
              </div>
            )}
          </div>

          {/* Multimodal */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">🎙️ Entiende voz e imágenes</h3>
            <div className="form-hint" style={{ marginBottom: 14 }}>El bot puede escuchar notas de voz y analizar fotos que envíe el cliente.</div>
            {[
              { k: 'entenderAudios',   t: '🎤 Transcribir notas de voz', d: 'Escucha los audios y responde como si el cliente los hubiera escrito' },
              { k: 'entenderImagenes', t: '🖼️ Analizar imágenes', d: 'Interpreta fotos de productos, comprobantes, capturas, etc.' },
            ].map(o => (
              <div key={o.k} className="flex justify-between items-center" style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ paddingRight: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{o.t}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{o.d}</div>
                </div>
                <div className={`toggle ${config[o.k] ? 'on' : ''}`} onClick={() => upd(o.k, !config[o.k])} />
              </div>
            ))}
          </div>

          {/* Precisión y escalamiento */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">🎯 Precisión y derivación a humano</h3>
            {[
              { k: 'antiAlucinacion', t: '🛡️ Anti-alucinación', d: 'Prohíbe inventar precios, stock o datos. Solo afirma lo que sabe o consulta por API' },
              { k: 'escalarSiNoSabe', t: '🙋 Derivar si no sabe', d: 'Cuando no puede resolver con certeza, conecta automáticamente con un agente humano' },
            ].map(o => (
              <div key={o.k} className="flex justify-between items-center" style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ paddingRight: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{o.t}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{o.d}</div>
                </div>
                <div className={`toggle ${config[o.k] ? 'on' : ''}`} onClick={() => upd(o.k, !config[o.k])} />
              </div>
            ))}
          </div>

          {/* Estilo de respuesta */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">🎨 Estilo de las respuestas</h3>
            <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 4 }}>
              <div>
                <label className="form-label">Longitud</label>
                <select className="select" value={config.longitudRespuesta || 'media'} onChange={e => upd('longitudRespuesta', e.target.value)}>
                  <option value="corta">Corta — directo al grano</option>
                  <option value="media">Media — recomendado</option>
                  <option value="larga">Larga — más detalle</option>
                </select>
              </div>
              <div>
                <label className="form-label">Tratamiento</label>
                <select className="select" value={config.tratamiento || 'tu'} onChange={e => upd('tratamiento', e.target.value)}>
                  <option value="tu">Tú — cercano</option>
                  <option value="usted">Usted — formal</option>
                </select>
              </div>
              <div>
                <label className="form-label">Moneda</label>
                <input className="input" value={config.moneda || ''} onChange={e => upd('moneda', e.target.value)} placeholder="Ej: Bs, $, S/, US$" />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>El bot escribirá los precios con este símbolo, nunca inventará otro.</div>
              </div>
            </div>
            <div className="flex justify-between items-center" style={{ padding: '14px 0 0' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>😊 Usar emojis</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Da calidez a las respuestas (con moderación)</div>
              </div>
              <div className={`toggle ${config.usarEmojis ? 'on' : ''}`} onClick={() => upd('usarEmojis', !config.usarEmojis)} />
            </div>
          </div>

          {config.geminiApiKeySet && config.proveedorIA !== 'gemini' && (
            <div className="card" style={{ marginBottom: 16 }}>
              <h3 className="card-title">🔑 API Key antigua de Gemini</h3>
              <div className="form-hint">
                Este número tiene guardada una API key de Gemini de la configuración anterior. Ya no se usa porque ahora el proveedor es <strong>{proveedorActual?.nombre}</strong>.
                Se conserva por si quieres volver a Gemini directo.
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Archivos y Enlaces ── */}
      {tab === 'medios' && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="flex justify-between items-center" style={{ marginBottom: 6 }}>
              <div style={{ paddingRight: 16 }}>
                <h3 className="card-title" style={{ marginBottom: 2 }}>📎 El bot puede enviar archivos</h3>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Fotos, audios, videos y catálogos en PDF que el bot manda por WhatsApp cuando hacen falta.</div>
              </div>
              <div className={`toggle ${config.permitirEnviarMedia !== false ? 'on' : ''}`} onClick={() => upd('permitirEnviarMedia', config.permitirEnviarMedia === false)} />
            </div>
            <div style={{ padding: 10, borderRadius: 10, background: 'var(--bg)', border: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              🛡️ <strong>Cómo se evita que invente cosas:</strong> el bot no puede escribir una dirección de archivo. Solo puede elegir uno de los archivos de esta lista por su <em>clave</em>. Si intenta usar una clave que no existe, no se envía nada.
            </div>
          </div>

          {config.permitirEnviarMedia !== false && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="flex justify-between items-center" style={{ marginBottom: 14 }}>
                <h3 className="card-title" style={{ marginBottom: 0 }}>🗂️ Biblioteca de archivos</h3>
                <label className="btn btn-secondary" style={{ cursor: subiendoMedia ? 'wait' : 'pointer', margin: 0 }}>
                  {subiendoMedia ? '⏳ Subiendo…' : '+ Subir archivo'}
                  <input type="file" hidden disabled={subiendoMedia}
                    accept="image/*,audio/*,video/*,application/pdf"
                    onChange={e => { subirMedia(e.target.files?.[0]); e.target.value = '' }} />
                </label>
              </div>

              {!(config.bibliotecaMedios || []).length && (
                <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>
                  Todavía no hay archivos. Sube una foto, un audio de bienvenida o tu catálogo en PDF.
                </div>
              )}

              {(config.bibliotecaMedios || []).map(m => (
                <div key={m.id} style={{ padding: 12, borderRadius: 12, border: '1px solid var(--border)', marginBottom: 10 }}>
                  <div className="flex justify-between items-center" style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 18 }}>
                        {m.tipo === 'imagen' ? '🖼️' : m.tipo === 'audio' ? '🎵' : m.tipo === 'video' ? '🎬' : '📄'}
                      </span>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{m.nombre}</div>
                        <a href={m.url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: 'var(--primary)' }}>Ver archivo</a>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div className={`toggle ${m.activo !== false ? 'on' : ''}`} onClick={() => updMedia(m.id, 'activo', m.activo === false)} />
                      <button className="btn-icon" onClick={() => removeMedia(m.id)} title="Eliminar">🗑️</button>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div>
                      <label className="form-label" style={{ fontSize: 11 }}>Clave (la usa el bot)</label>
                      <input className="form-input" value={m.clave || ''}
                        onChange={e => updMedia(m.id, 'clave', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))} />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: 11 }}>Texto que lo acompaña (opcional)</label>
                      <input className="form-input" placeholder="ej: Aquí tienes nuestro catálogo 📄"
                        value={m.caption || ''} onChange={e => updMedia(m.id, 'caption', e.target.value)} />
                    </div>
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <label className="form-label" style={{ fontSize: 11 }}>¿Cuándo debe enviarlo? — esto es lo que lee el bot para decidir</label>
                    <input className="form-input" placeholder="ej: Catálogo completo con todos los productos y precios. Enviar cuando pidan ver el catálogo o la lista de precios."
                      value={m.descripcion || ''} onChange={e => updMedia(m.id, 'descripcion', e.target.value)} />
                    {!m.descripcion && (
                      <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 4, fontWeight: 600 }}>
                        ⚠️ Sin descripción el bot no sabrá cuándo usarlo.
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Enlaces autorizados */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="flex justify-between items-center" style={{ marginBottom: 6 }}>
              <div style={{ paddingRight: 16 }}>
                <h3 className="card-title" style={{ marginBottom: 2 }}>🔗 Enlaces que el bot puede compartir</h3>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Tu web, catálogo online, ubicación en Maps, redes sociales…</div>
              </div>
              <button className="btn btn-secondary" onClick={addEnlace}>+ Agregar</button>
            </div>
            <div style={{ padding: 10, borderRadius: 10, background: 'var(--bg)', border: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 14 }}>
              🛡️ Si esta lista está vacía, el bot tiene <strong>prohibido escribir cualquier enlace</strong>. Así no puede inventar una dirección web que no existe.
            </div>

            {(config.enlacesAutorizados || []).map(e => (
              <div key={e.id} style={{ padding: 12, borderRadius: 12, border: '1px solid var(--border)', marginBottom: 10 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: 10, alignItems: 'end' }}>
                  <div>
                    <label className="form-label" style={{ fontSize: 11 }}>Nombre</label>
                    <input className="form-input" placeholder="ej: Tienda online"
                      value={e.titulo || ''} onChange={ev => updEnlace(e.id, 'titulo', ev.target.value)} />
                  </div>
                  <div>
                    <label className="form-label" style={{ fontSize: 11 }}>Dirección</label>
                    <input className="form-input" placeholder="https://…"
                      value={e.url || ''} onChange={ev => updEnlace(e.id, 'url', ev.target.value)} />
                  </div>
                  <div className={`toggle ${e.activo !== false ? 'on' : ''}`} onClick={() => updEnlace(e.id, 'activo', e.activo === false)} />
                  <button className="btn-icon" onClick={() => removeEnlace(e.id)} title="Eliminar">🗑️</button>
                </div>
                <div style={{ marginTop: 10 }}>
                  <label className="form-label" style={{ fontSize: 11 }}>¿Cuándo compartirlo? (opcional)</label>
                  <input className="form-input" placeholder="ej: Cuando pidan ver todos los productos o comprar por la web"
                    value={e.descripcion || ''} onChange={ev => updEnlace(e.id, 'descripcion', ev.target.value)} />
                </div>
              </div>
            ))}

            {!(config.enlacesAutorizados || []).length && (
              <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 13 }}>
                Sin enlaces configurados — el bot no compartirá ninguna dirección web.
              </div>
            )}
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="card-title">💬 Mensajes seguidos</h3>
            <label className="form-label">Máximo de mensajes seguidos por respuesta: {config.maxBurbujas || 4}</label>
            <input type="range" min="1" max="6" step="1" value={config.maxBurbujas || 4}
              onChange={ev => upd('maxBurbujas', parseInt(ev.target.value))} style={{ width: '100%' }} />
            <div className="form-hint" style={{ marginTop: 8 }}>
              El bot separa sus ideas en varios mensajes cortos, como escribiría una persona, en vez de mandar un bloque de texto enorme.
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Pedidos y Pagos ── */}
      {tab === 'pedidos' && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="flex justify-between items-center">
              <div>
                <h3 className="card-title" style={{ marginBottom: 2 }}>🛒 El bot puede tomar pedidos</h3>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>El bot arma el pedido, pide los datos de entrega y envía los datos de pago. El pedido cae en tu panel como "pendiente" hasta que confirmes.</div>
              </div>
              <div className={`toggle ${config.tomarPedidos ? 'on' : ''}`} onClick={() => upd('tomarPedidos', !config.tomarPedidos)} />
            </div>
          </div>

          {config.tomarPedidos && (
            <>
              <div className="card" style={{ marginBottom: 16 }}>
                <h3 className="card-title">🚚 Entrega y envío</h3>
                <div className="form-group">
                  <label className="form-label">Ciudad de la tienda</label>
                  <input className="input" value={config.ciudadOrigen || ''} onChange={e => upd('ciudadOrigen', e.target.value)} placeholder="Ej: Potosí" />
                  <div className="form-hint">Si el cliente es de esta ciudad → entrega a domicilio (pide dirección y referencia). Si es de otra ciudad → encomienda (pide ciudad).</div>
                </div>
                <div className="form-group">
                  <label className="form-label">Nota de envío (opcional)</label>
                  <textarea className="input" rows={2} value={config.envioInfo || ''} onChange={e => upd('envioInfo', e.target.value)} placeholder="Ej: Entrega a domicilio GRATIS en Potosí. A otras ciudades: encomienda por cobrar en destino con la transportadora." />
                  <div className="form-hint">El bot usará esto para explicar el envío al cliente.</div>
                </div>
              </div>

              <div className="card" style={{ marginBottom: 16 }}>
                <div className="flex justify-between items-center" style={{ marginBottom: 8 }}>
                  <h3 className="card-title" style={{ margin: 0 }}>💳 Métodos de pago</h3>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>El bot enviará SOLO los métodos que agregues aquí. Nada más.</div>

                {(Array.isArray(config.pagosBot) ? config.pagosBot : []).map(m => (
                  <div key={m.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12, marginBottom: 10 }}>
                    <div className="flex justify-between items-center" style={{ marginBottom: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: 13 }}>{m.tipo === 'qr' ? '📷 QR' : m.tipo === 'banco' ? '🏦 Banco' : '💰 PayPal'}</span>
                      <button className="btn btn-sm" style={{ color: 'var(--destructive)' }} onClick={() => removeMetodoPago(m.id)}>Eliminar</button>
                    </div>
                    <div className="form-group" style={{ marginBottom: 8 }}>
                      <label className="form-label">Nombre visible</label>
                      <input className="input" value={m.label || ''} onChange={e => updMetodoPago(m.id, 'label', e.target.value)} placeholder="Ej: Pago con QR" />
                    </div>
                    {m.tipo === 'qr' && (
                      <div className="form-group">
                        <label className="form-label">Imagen del QR</label>
                        {m.imagenUrl && <div style={{ marginBottom: 8 }}><img src={m.imagenUrl} alt="QR" style={{ width: 120, height: 120, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 8 }} /></div>}
                        <input type="file" accept="image/*" onChange={e => subirQR(m.id, e.target.files?.[0])} />
                        <div className="form-hint">Sube aquí tu QR de pago. El bot lo enviará al cliente al cerrar el pedido.</div>
                      </div>
                    )}
                    {m.tipo === 'banco' && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div className="form-group"><label className="form-label">Banco</label><input className="input" value={m.banco || ''} onChange={e => updMetodoPago(m.id, 'banco', e.target.value)} placeholder="Ej: BNB" /></div>
                        <div className="form-group"><label className="form-label">N° de cuenta</label><input className="input" value={m.cuenta || ''} onChange={e => updMetodoPago(m.id, 'cuenta', e.target.value)} placeholder="1234567890" /></div>
                        <div className="form-group"><label className="form-label">Titular</label><input className="input" value={m.titular || ''} onChange={e => updMetodoPago(m.id, 'titular', e.target.value)} placeholder="Nombre del titular" /></div>
                        <div className="form-group"><label className="form-label">CI / NIT (opcional)</label><input className="input" value={m.ci || ''} onChange={e => updMetodoPago(m.id, 'ci', e.target.value)} placeholder="" /></div>
                      </div>
                    )}
                    {m.tipo === 'paypal' && (
                      <div className="form-group"><label className="form-label">Correo / enlace PayPal</label><input className="input" value={m.email || ''} onChange={e => updMetodoPago(m.id, 'email', e.target.value)} placeholder="tucorreo@paypal.com" /></div>
                    )}
                  </div>
                ))}

                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <button className="btn" onClick={() => addMetodoPago('qr')}>+ QR</button>
                  <button className="btn" onClick={() => addMetodoPago('banco')}>+ Cuenta bancaria</button>
                  <button className="btn" onClick={() => addMetodoPago('paypal')}>+ PayPal</button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Tab: APIs Externas ── */}
      {tab === 'apis' && (
        <div>
          {/* Banner explicativo */}
          <div style={{ background: 'linear-gradient(135deg, #eff6ff, #dbeafe)', border: '1px solid #93c5fd', borderRadius: 12, padding: '16px 20px', marginBottom: 20, display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ fontSize: 32, flexShrink: 0 }}>🔌</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1e40af', marginBottom: 4 }}>APIs externas en tiempo real</div>
              <div style={{ fontSize: 13, color: '#1e3a8a', lineHeight: 1.6 }}>
                Conecta tu sistema de stock, precios, pedidos o cualquier API REST. Cuando el cliente pregunte por disponibilidad o info en tiempo real,
                <strong> el bot consultará tu API automáticamente</strong> antes de responder. Funciona con cualquier API que devuelva JSON.
              </div>
              <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['ERP / Stock', 'WooCommerce', 'Shopify', 'Odoo', 'Sistema propio', 'Reservas', 'Clima', 'Tipo de cambio'].map(e => (
                  <span key={e} style={{ background: 'rgba(59,130,246,0.15)', color: '#1d4ed8', fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20 }}>{e}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Cómo diseñar el endpoint para que el bot no se equivoque */}
          <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 12, padding: '14px 18px', marginBottom: 20, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={{ fontSize: 24, flexShrink: 0 }}>🎯</div>
            <div style={{ fontSize: 13, color: '#78350f', lineHeight: 1.6 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Que tu endpoint devuelva la respuesta, no los datos crudos</div>
              Lo comprobamos midiendo: si el endpoint devuelve un montón de campos parecidos para que el bot elija,
              <strong> se equivoca de campo y da una cifra incorrecta con total seguridad</strong>. En cambio, si devuelve el dato ya resuelto y bien etiquetado, acierta siempre.
              <div style={{ marginTop: 8 }}>
                ✅ <code>{'{ "precio": 95, "moneda": "Bs", "hay_stock": true }'}</code>
                <br />
                ❌ <code>{'{ "rates": { "AED": 3.67, "BOB": 11.2, "VES": 849.5, … 160 más } }'}</code>
              </div>
              <div style={{ marginTop: 8 }}>
                Un endpoint por pregunta concreta funciona mucho mejor que uno genérico que lo devuelve todo. Usa el botón <strong>Probar</strong> de cada endpoint para ver exactamente qué recibe el bot.
              </div>
            </div>
          </div>

          {/* Lista de APIs */}
          {(config.apisExternas || []).length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--text-muted)', border: '2px dashed var(--border)', borderRadius: 16 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🔌</div>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Sin APIs configuradas</div>
              <div style={{ fontSize: 14, marginBottom: 20 }}>Agrega tu primera API para que el bot consulte datos en tiempo real</div>
              <button className="btn btn-primary" onClick={addApi}>+ Agregar primera API</button>
            </div>
          )}

          {(config.apisExternas || []).map(apiObj => (
            <div key={apiObj.id} className="card" style={{ marginBottom: 14, border: apiObj.activo ? '1px solid var(--border)' : '1px solid var(--border)', opacity: apiObj.activo ? 1 : 0.6 }}>
              {/* Header de la API */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: apiExpandida === apiObj.id ? 20 : 0 }}>
                <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setApiExpandida(apiExpandida === apiObj.id ? null : apiObj.id)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 20 }}>🔗</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{apiObj.nombre || 'Nueva API'}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {apiObj.urlBase || 'URL no configurada'} · {(apiObj.endpoints || []).length} endpoint{(apiObj.endpoints || []).length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: apiObj.activo ? '#16a34a' : '#94a3b8' }}>
                    {apiObj.activo ? '● Activa' : '● Inactiva'}
                  </span>
                  <div className={`toggle ${apiObj.activo ? 'on' : ''}`} onClick={() => updApi(apiObj.id, 'activo', !apiObj.activo)} />
                  <button className="btn" style={{ fontSize: 12, padding: '5px 10px', color: '#ef4444', borderColor: '#fca5a5' }}
                    onClick={() => { if (window.confirm('¿Eliminar esta API?')) removeApi(apiObj.id) }}>
                    🗑
                  </button>
                  <span style={{ cursor: 'pointer', fontSize: 18, color: 'var(--text-muted)', userSelect: 'none' }}
                    onClick={() => setApiExpandida(apiExpandida === apiObj.id ? null : apiObj.id)}>
                    {apiExpandida === apiObj.id ? '▲' : '▼'}
                  </span>
                </div>
              </div>

              {/* Cuerpo expandido */}
              {apiExpandida === apiObj.id && (
                <div>
                  <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Nombre de la API *</label>
                      <input className="input" value={apiObj.nombre} onChange={e => updApi(apiObj.id, 'nombre', e.target.value)}
                        placeholder="Ej: Sistema de Stock" />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">URL Base *</label>
                      <input className="input" value={apiObj.urlBase} onChange={e => updApi(apiObj.id, 'urlBase', e.target.value)}
                        placeholder="https://mitienda.com/api/v1" />
                    </div>
                  </div>

                  <div className="form-group" style={{ marginBottom: 14 }}>
                    <label className="form-label">¿Qué datos provee? (para que Gemini sepa cuándo usarla)</label>
                    <input className="input" value={apiObj.descripcion} onChange={e => updApi(apiObj.id, 'descripcion', e.target.value)}
                      placeholder="Ej: Provee stock en tiempo real y precios actuales de productos del catálogo" />
                  </div>

                  {/* Autenticación */}
                  <div style={{ background: 'var(--bg)', borderRadius: 10, padding: 14, marginBottom: 14 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>🔐 Autenticación</div>
                    <div className="api-auth-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" style={{ fontSize: 11 }}>Tipo</label>
                        <select className="select" value={apiObj.authTipo} onChange={e => updApi(apiObj.id, 'authTipo', e.target.value)}>
                          {AUTH_TIPOS.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
                        </select>
                      </div>
                      {apiObj.authTipo !== 'none' && apiObj.authTipo !== 'bearer' && (
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label" style={{ fontSize: 11 }}>
                            {apiObj.authTipo === 'query' ? 'Nombre del parámetro' : 'Nombre del Header'}
                          </label>
                          <input className="input" value={apiObj.authHeader} onChange={e => updApi(apiObj.id, 'authHeader', e.target.value)}
                            placeholder={apiObj.authTipo === 'query' ? 'api_key' : 'X-API-Key'} />
                        </div>
                      )}
                      {apiObj.authTipo !== 'none' && (
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label" style={{ fontSize: 11 }}>
                            {apiObj.authTipo === 'basic' ? 'usuario:contraseña' : apiObj.authTipo === 'bearer' ? 'Token' : 'Valor / API Key'}
                          </label>
                          <input type="password" className="input" value={apiObj.authValor} onChange={e => updApi(apiObj.id, 'authValor', e.target.value)}
                            placeholder={apiObj.authTipo === 'basic' ? 'admin:password123' : 'sk-xxxx...'} />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Endpoints */}
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>📡 Endpoints ({(apiObj.endpoints || []).length})</span>
                    <button className="btn" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => addEndpoint(apiObj.id)}>
                      + Agregar endpoint
                    </button>
                  </div>

                  {(apiObj.endpoints || []).length === 0 && (
                    <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', border: '1px dashed var(--border)', borderRadius: 10, fontSize: 13, marginBottom: 10 }}>
                      Sin endpoints. Agrega al menos uno para que Gemini pueda usarlo.
                    </div>
                  )}

                  {(apiObj.endpoints || []).map(ep => {
                    const testKey = `${apiObj.id}_${ep.id}`
                    const testResult = testResultados[testKey]
                    return (
                      <div key={ep.id} style={{ border: '1px solid var(--border)', borderRadius: 10, marginBottom: 10, overflow: 'hidden' }}>
                        {/* Header endpoint */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--bg)', cursor: 'pointer' }}
                          onClick={() => setEpExpandido(epExpandido === ep.id ? null : ep.id)}>
                          <span style={{
                            background: ep.metodo === 'GET' ? '#dcfce7' : ep.metodo === 'POST' ? '#dbeafe' : '#fef3c7',
                            color: ep.metodo === 'GET' ? '#15803d' : ep.metodo === 'POST' ? '#1d4ed8' : '#92400e',
                            fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 4,
                          }}>{ep.metodo}</span>
                          <code style={{ fontSize: 12, color: 'var(--text-soft)', flex: 1 }}>{ep.path || '/endpoint'}</code>
                          <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1 }}>{ep.descripcion || 'Sin descripción'}</span>
                          <button className="btn" style={{ fontSize: 11, padding: '4px 10px', color: '#ef4444' }}
                            onClick={e => { e.stopPropagation(); removeEndpoint(apiObj.id, ep.id) }}>✕</button>
                          <span style={{ color: 'var(--text-muted)' }}>{epExpandido === ep.id ? '▲' : '▼'}</span>
                        </div>

                        {/* Body endpoint */}
                        {epExpandido === ep.id && (
                          <div style={{ padding: 14 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 10, marginBottom: 12 }}>
                              <div className="form-group" style={{ margin: 0 }}>
                                <label className="form-label" style={{ fontSize: 11 }}>Método</label>
                                <select className="select" value={ep.metodo} onChange={e => updEndpoint(apiObj.id, ep.id, 'metodo', e.target.value)}>
                                  {['GET', 'POST', 'PUT', 'DELETE'].map(m => <option key={m}>{m}</option>)}
                                </select>
                              </div>
                              <div className="form-group" style={{ margin: 0 }}>
                                <label className="form-label" style={{ fontSize: 11 }}>Path del endpoint</label>
                                <input className="input" value={ep.path} onChange={e => updEndpoint(apiObj.id, ep.id, 'path', e.target.value)}
                                  placeholder="/products/stock  o  /products/{id}/stock" />
                              </div>
                            </div>

                            <div className="form-group" style={{ marginBottom: 12 }}>
                              <label className="form-label" style={{ fontSize: 11 }}>Descripción para Gemini (¿cuándo debe llamar a este endpoint?)</label>
                              <input className="input" value={ep.descripcion} onChange={e => updEndpoint(apiObj.id, ep.id, 'descripcion', e.target.value)}
                                placeholder="Ej: Consultar stock disponible de un producto por nombre o código" />
                            </div>

                            {/* Parámetros */}
                            <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
                              <span>Parámetros que puede enviar Gemini</span>
                              <button className="btn" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => addParam(apiObj.id, ep.id)}>+ Parámetro</button>
                            </div>

                            {(ep.parametros || []).length === 0 && (
                              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10, fontStyle: 'italic' }}>
                                Sin parámetros — Gemini llamará sin argumentos
                              </div>
                            )}

                            {(ep.parametros || []).map((param, idx) => (
                              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                                <input className="input" value={param.nombre} onChange={e => updParam(apiObj.id, ep.id, idx, 'nombre', e.target.value)}
                                  placeholder="nombre" style={{ fontSize: 12 }} />
                                <input className="input" value={param.descripcion} onChange={e => updParam(apiObj.id, ep.id, idx, 'descripcion', e.target.value)}
                                  placeholder="Ej: Nombre del producto a consultar" style={{ fontSize: 12 }} />
                                <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                  <input type="checkbox" checked={param.requerido} onChange={e => updParam(apiObj.id, ep.id, idx, 'requerido', e.target.checked)} />
                                  Requerido
                                </label>
                                <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 16 }}
                                  onClick={() => removeParam(apiObj.id, ep.id, idx)}>✕</button>
                              </div>
                            ))}

                            {/* Botón probar */}
                            <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: testResult ? 10 : 0 }}>
                                <button className="btn" style={{ fontSize: 12 }}
                                  onClick={() => testEndpoint(apiObj, ep)}
                                  disabled={testando[testKey] || !apiObj.urlBase || !ep.path}>
                                  {testando[testKey] ? '⏳ Probando...' : '▶ Probar conexión'}
                                </button>
                                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                  Llamará a: <code style={{ fontSize: 11 }}>{apiObj.urlBase}{ep.path}</code>
                                </span>
                              </div>
                              {testResult && (
                                <div style={{
                                  background: testResult.ok ? '#f0fdf4' : '#fef2f2',
                                  border: `1px solid ${testResult.ok ? '#a7f3d0' : '#fca5a5'}`,
                                  borderRadius: 8, padding: 12,
                                }}>
                                  <div style={{ fontWeight: 700, fontSize: 12, color: testResult.ok ? '#166534' : '#991b1b', marginBottom: 6 }}>
                                    {testResult.ok ? '✅ Conexión exitosa' : '❌ Error de conexión'}
                                  </div>
                                  {testResult.error && <div style={{ fontSize: 12, color: '#991b1b' }}>{testResult.error}</div>}
                                  {testResult.data && (
                                    <pre style={{ fontSize: 11, margin: 0, maxHeight: 150, overflow: 'auto', color: '#374151' }}>
                                      {JSON.stringify(testResult.data, null, 2)}
                                    </pre>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          ))}

          {(config.apisExternas || []).length > 0 && (
            <button className="btn" style={{ width: '100%', justifyContent: 'center', borderStyle: 'dashed' }} onClick={addApi}>
              + Agregar otra API
            </button>
          )}

          {/* Tips */}
          <div style={{ marginTop: 20, background: '#fafafa', border: '1px solid var(--border)', borderRadius: 12, padding: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>💡 Ejemplos de uso</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
              {[
                { icon: '📦', title: 'Stock en tiempo real', desc: 'GET /products/{nombre}/stock → Gemini consulta si hay disponibilidad antes de confirmar al cliente' },
                { icon: '💰', title: 'Precios dinámicos', desc: 'GET /prices?product={nombre} → El bot siempre da el precio actualizado sin que tú lo edites' },
                { icon: '📋', title: 'Estado de pedido', desc: 'GET /orders/{codigo} → El cliente pregunta "¿dónde está mi pedido?" y el bot responde con el estado real' },
                { icon: '📅', title: 'Disponibilidad de citas', desc: 'GET /availability?date={fecha} → Para agendar, el bot revisa qué horarios están libres' },
              ].map(e => (
                <div key={e.title} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
                  <div style={{ fontSize: 20, marginBottom: 6 }}>{e.icon}</div>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{e.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>{e.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal: ver prompt generado */}
      {verPrompt && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 720, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>👁 Prompt generado automáticamente</h3>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>Esto es exactamente lo que Gemini recibe como instrucción</p>
              </div>
              <button className="btn" onClick={() => setVerPrompt(false)}>✕ Cerrar</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12.5, lineHeight: 1.7, color: 'var(--text)', fontFamily: 'monospace', margin: 0 }}>{promptGenerado}</pre>
            </div>
            <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => { navigator.clipboard.writeText(promptGenerado); toast.success('Copiado') }}>📋 Copiar</button>
              <button className="btn btn-primary" onClick={() => setVerPrompt(false)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* Footer guardar — solo en página completa */}
      {!embedded && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 20, borderTop: '1px solid var(--border)', marginTop: 8 }}>
          <button className="btn" onClick={() => navigate('/sesiones')}>← Cancelar</button>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn" onClick={generarYVerPrompt}>👁 Vista previa del prompt</button>
            <button className="btn btn-primary btn-lg" onClick={guardar} disabled={guardando}>
              {guardando ? 'Guardando...' : '💾 Guardar configuración'}
            </button>
          </div>
        </div>
      )}
    </>
  )

  if (embedded) return inner
  return <div className="page" style={{ maxWidth: 960 }}>{inner}</div>
}

function calcularCompletitud(config) {
  const campos = [
    config.nombreBot, config.nombreNegocio, config.descripcionNegocio,
    config.productosServicios, config.procesosVenta, config.politicasNegocio,
    config.metodosPago, config.datosContacto, config.preguntasFrecuentes,
    config.restricciones, config.horario, config.mensajeBienvenida,
  ]
  const llenos = campos.filter(c => typeof c === 'string' && c.trim().length > 10).length
  return Math.round((llenos / campos.length) * 100)
}
